/**
 * lit_smoke — SIX-WAY lit smoke puffs for impacts (shader additions wave,
 * 2026-09-15). The engine's `GpuParticleMaterial` reconstructs directional
 * lighting from two baked flipbooks (`public/vfx/smoke6_{a,b}.png`, baked by
 * tools/asset-pipeline/gen-sixway-smoke.mjs); this is the pooled emitter set
 * that spawns a puff where a bolt lands, coloured by what it struck
 * (surface_response's `dust`).
 *
 * ONE emitter per dust COLOUR (the ramp colour is per material), created on
 * first use and kept for the session (the GPU shield law: nothing ever drawn
 * is destroyed). WebGPU only: the CPU adapter draws plain sprites and has no
 * six-way path, so on WebGL2 the impact keeps its existing burst and this
 * spawns nothing — counted in `stage5Stats.sixWayPuffs` either way so a probe
 * can tell. The kernels spawn in RENDER space, so the impact's ABSOLUTE point
 * is seated at `+ scene.position` (the weather module's FRAME law).
 *
 * The light the smoke is lit by is written by AtlasShaderFxModule into every
 * material's `sixWayUniforms` (registered in `sixWayHandles`).
 */
import * as THREE from 'three/webgpu';
import type { App } from 'enginex';
import { GpuParticleEmitter } from 'enginex/particles/gpu/GpuParticleEmitter.js';
import { atlas } from '../state.js';
import { activePreset } from '../lib/quality_tiers.js';
import { sixWayHandles, stage5Stats } from './stage5_fx.js';

interface Store { app: App | null; maps: { a: THREE.Texture; b: THREE.Texture } | null; loading: boolean; byColor: Map<number, GpuParticleEmitter>; lastMs: number }
const g = globalThis as unknown as { __atlasLitSmoke?: Store };
const store = (): Store => (g.__atlasLitSmoke ??= { app: null, maps: null, loading: false, byColor: new Map(), lastMs: 0 });

const base = (): string => ((import.meta as unknown as { env?: { BASE_URL?: string } }).env?.BASE_URL ?? '/').replace(/\/?$/, '/');

function loadMaps(s: Store): void {
  if (s.maps || s.loading) return;
  s.loading = true;
  const loader = new THREE.TextureLoader();
  const a = loader.load(`${base()}vfx/smoke6_a.png`);
  const b = loader.load(`${base()}vfx/smoke6_b.png`);
  for (const t of [a, b]) { t.colorSpace = THREE.NoColorSpace; t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping; t.generateMipmaps = true; t.minFilter = THREE.LinearMipmapLinearFilter; }
  s.maps = { a, b };
  s.loading = false;
}

function emitterFor(s: Store, color: number): GpuParticleEmitter | null {
  const app = s.app;
  if (!app || !s.maps) return null;
  let e = s.byColor.get(color);
  if (e) return e;
  if (s.byColor.size >= 6) e = s.byColor.values().next().value as GpuParticleEmitter; // bounded: reuse the first
  if (!e) {
    e = new GpuParticleEmitter(app.renderer as never, {
      maxParticles: 96,
      lifetime: { min: 1.3, max: 2.2 },
      startSpeed: { min: 0.5, max: 1.6 },
      startSize: { min: 0.8, max: 1.4 },
      startColor: new THREE.Color(color),
      gravityModifier: -0.04,                 // a slow rise
      simulationSpace: 'world',
      shape: { type: 'box', halfExtents: new THREE.Vector3(0.25, 0.15, 0.25) },
      emission: { rateOverTime: 0 },
      sizeOverLifetime: [{ time: 0, value: 0.55 }, { time: 0.35, value: 1 }, { time: 1, value: 1.7 }],
      alphaOverLifetime: [{ time: 0, value: 0.9 }, { time: 0.6, value: 0.8 }, { time: 1, value: 0 }],
      noise: { strength: 0.35, frequency: 0.7, scrollSpeed: 0.3 },
      renderMode: 'billboard',
      blending: THREE.NormalBlending,
      sixWay: { mapA: s.maps.a, mapB: s.maps.b, columns: 8, rows: 8 },
    } as never);
    (e.mesh.material as THREE.Material).premultipliedAlpha = true;
    e.mesh.renderOrder = 12; // under the additive bolts (15), over the world
    app.world.scene.add(e.mesh);
    const uni = (e.mesh.material as { sixWayUniforms?: unknown }).sixWayUniforms;
    if (uni) sixWayHandles.add(uni as never);
    stage5Stats.sixWayEmitters++;
    s.byColor.set(color, e);
  }
  return e;
}

/** The six-way flipbook pair (loaded once, shared with the fire smoke column — lib/vfx/fire_particles.ts). */
export function litSmokeMaps(): { a: THREE.Texture; b: THREE.Texture } | null {
  const s = store();
  loadMaps(s);
  return s.maps;
}

/** Arm the system (once, from a module that has the app). */
export function initLitSmoke(app: App): void { const s = store(); s.app = app; }

/** A puff at an ABSOLUTE point. Silent on WebGL2 or with the flag off. */
export function litSmokePuff(x: number, y: number, z: number, dust: { size: number; color: number; opacity: number }): boolean {
  const s = store();
  const app = s.app;
  if (!app || !activePreset().litSmoke || !(app.renderer as { isWebGPU?: boolean }).isWebGPU) return false;
  loadMaps(s);
  const e = emitterFor(s, dust.color);
  if (!e) return false;
  const sp = app.world.scene.position;
  e.worldMatrix.makeTranslation(x + sp.x, y + sp.y + 0.2, z + sp.z);
  e.syncSpawn();
  const n = Math.max(4, Math.min(14, Math.round(dust.size * 9)));
  e.emit(n);
  e.play();
  stage5Stats.sixWayPuffs++;
  return true;
}

/** Advance every puff emitter — once per frame from the shader-FX tick. */
export function updateLitSmoke(): void {
  const s = store();
  if (!s.byColor.size) return;
  const now = performance.now();
  const dt = s.lastMs ? Math.min(0.1, (now - s.lastMs) / 1000) : 1 / 60;
  s.lastMs = now;
  for (const e of s.byColor.values()) e.update(dt);
}

/** Diagnostics. */
export function litSmokeStats(): { emitters: number; active: number; loaded: boolean } {
  const s = store();
  let active = 0;
  for (const e of s.byColor.values()) active += e.getActiveCount();
  return { emitters: s.byColor.size, active, loaded: !!s.maps };
}

/** The player-relative check probes use: is the nearest puff within reach of the player? */
export function litSmokeNearPlayer(): boolean {
  const s = store();
  for (const e of s.byColor.values()) {
    const p = new THREE.Vector3().setFromMatrixPosition(e.worldMatrix);
    const sp = s.app?.world.scene.position ?? { x: 0, y: 0, z: 0 };
    if (Math.hypot(p.x - sp.x - atlas.localX, p.z - sp.z - atlas.localZ) < 60) return true;
  }
  return false;
}
